import {useState, useCallback, useMemo, useEffect} from 'react';
import {monthNow} from '../assets/utils/dateUtils';

// Функция для получения количества дней в месяце
export const getDaysInMonth = (monthString: string): number => {
	if (!monthString) return 31;
	
	const date = new Date(monthString);
	const year = date.getFullYear();
	const month = date.getMonth();
	
	// Последний день месяца
	const lastDay = new Date(year, month + 1, 0);
	return lastDay.getDate();
};

// Функция для формирования даты из месяца и дня
export const formatDateFromMonthAndDay = (monthString: string, day: number): string => {
	if (!monthString) return '';
	
	const date = new Date(monthString);
	const year = date.getFullYear();
	const month = date.getMonth() + 1; // getMonth() возвращает 0-11
	
	const dayStr = day.toString().padStart(2, '0');
	const monthStr = month.toString().padStart(2, '0');
	
	return `${year}-${monthStr}-${dayStr}`;
};

export interface UseMonthRangeFilterReturn {
	// Состояние
	month: string;
	daysInMonth: number;
	rangeValue: [number, number];
	
	// Сформированные даты
	fromDate: string;
	toDate: string;
	
	// Функции для изменения
	setMonth: (month: string) => void;
	setRangeValue: (range: [number, number]) => void;
	reset: () => void;
	
	// Адаптированные значения для RangeFilter (0-based индексы для слайдера)
	adaptedRangeValue: [number, number];
	// Значения для отображения в инпутах (1-based дни)
	displayRangeValue: [number, number];
	handleRangeChange: (newRange: [number, number]) => void;
}

export const useMonthRangeFilter = (
	initialMonth?: string
): UseMonthRangeFilterReturn => {
	const defaultMonth = initialMonth || monthNow();
	const defaultDaysInMonth = getDaysInMonth(defaultMonth);
	
	const [month, setMonthState] = useState<string>(defaultMonth);
	const [rangeValue, setRangeValueState] = useState<[number, number]>([
		1,
		defaultDaysInMonth,
	]);
	
	// Вычисляем количество дней в текущем месяце
	const daysInMonth = useMemo(() => getDaysInMonth(month), [month]);
	
	// Обновляем ползунки при изменении месяца
	const setMonth = useCallback((newMonth: string) => {
		setMonthState(newMonth);
		const newDaysInMonth = getDaysInMonth(newMonth);
		// Сбрасываем ползунки на весь месяц
		setRangeValueState([1, newDaysInMonth]);
	}, []);
	
	// Устанавливаем значения ползунков
	const setRangeValue = useCallback((newRange: [number, number]) => {
		setRangeValueState(newRange);
	}, []);
	
	// Обработка изменения ползунков (для RangeFilter с адаптером)
	// Принимает индексы [0..daysInMonth-1] и преобразует в дни [1..daysInMonth]
	const handleRangeChange = useCallback((newRange: [number, number]) => {
		// Преобразуем обратно: индекс -> день (прибавляем 1)
		// Ограничиваем значения: минимум 1, максимум daysInMonth
		const dayFrom = Math.max(1, Math.min(newRange[0] + 1, daysInMonth));
		const dayTo = Math.max(dayFrom, Math.min(newRange[1] + 1, daysInMonth));
		setRangeValueState([dayFrom, dayTo]);
	}, [daysInMonth]);
	
	// Адаптер значений для RangeFilter (дни месяца 1-based, RangeFilter 0-based)
	// RangeFilter работает с индексами 0..(daysInMonth-1), но мы храним дни 1..daysInMonth
	const adaptedRangeValue: [number, number] = useMemo(() => {
		// Преобразуем дни в индексы: день 1 -> индекс 0, день 31 -> индекс 30
		return [Math.max(0, rangeValue[0] - 1), Math.max(0, rangeValue[1] - 1)];
	}, [rangeValue]);
	
	// Адаптированные значения для отображения в инпутах (дни, а не индексы)
	const displayRangeValue: [number, number] = useMemo(() => {
		return [rangeValue[0], rangeValue[1]];
	}, [rangeValue]);
	
	// Формируем даты из месяца и дней
	const fromDate = useMemo(() => {
		return formatDateFromMonthAndDay(month, rangeValue[0]);
	}, [month, rangeValue]);
	
	const toDate = useMemo(() => {
		return formatDateFromMonthAndDay(month, rangeValue[1]);
	}, [month, rangeValue]);
	
	// Сброс к дефолтным значениям
	const reset = useCallback(() => {
		const resetMonth = monthNow();
		const resetDays = getDaysInMonth(resetMonth);
		setMonthState(resetMonth);
		setRangeValueState([1, resetDays]);
	}, []);
	
	// Синхронизируем ползунки при изменении количества дней в месяце
	useEffect(() => {
		if (rangeValue[1] > daysInMonth) {
			setRangeValueState([Math.min(rangeValue[0], daysInMonth), daysInMonth]);
		} else if (rangeValue[0] < 1) {
			setRangeValueState([1, Math.max(rangeValue[1], 1)]);
		}
	}, [daysInMonth, rangeValue]);
	
	return {
		month,
		daysInMonth,
		rangeValue,
		fromDate,
		toDate,
		setMonth,
		setRangeValue,
		reset,
		adaptedRangeValue,
		displayRangeValue,
		handleRangeChange,
	};
};

